import { addDays, compareAsc, compareDesc, isSameDay, startOfDay } from 'date-fns'
import { EnrichedIssueEntity, IssueEntity } from '../../types/IssueEntity'
import { calendarFiltersAtom } from '../../atoms/calendarAtom'
import { DropdownItem } from '../components/DropdownItem'
import { formatBigMoney } from '../../packages/utils/numberFormatters'
import { getIntlLocale, getSortLocale } from '../../i18n/localeUtils'
import { hasSubPeriodPrelPrice } from '../../utils/warrants'
import { hasSourceOfType } from '../../components/utils/calculateIssueStatus'
import { WarrantListItem } from '@finance-lab/utils/types'

export type ExtendedWarrantEntity = WarrantListItem & {
    activeSubscriptionPeriods: WarrantListItem['subscriptionPeriods']
    marketplaceName: string | null
}

export const getMonthName = (monthNumber: number, language?: string) => {
    const date = new Date()
    date.setMonth(monthNumber) // starts with 0, so 0 is January
    return date.toLocaleString(getIntlLocale(language), { month: 'long' })
}

export const shouldIncludeEntity = (entity: IssueEntity | WarrantListItem, startDate: Date, endDate: Date) => {
    const dates: Array<string | null> = []
    if ('type' in entity) {
        // If the entity contains the field type, it is an IssueEntity
        if (!['Rights issue', 'IPO'].includes(entity.type)) {
            return false
        }
        const { exRightsDate, incRightsDate, ipoTradingDate, rightsEndDate, subStartDate, subEndDate } = entity

        if (subStartDate === null && subEndDate === null) return false

        dates.push(exRightsDate, incRightsDate, ipoTradingDate, rightsEndDate, subStartDate, subEndDate)
    } else {
        const { subscriptionPeriods } = entity

        subscriptionPeriods.forEach((period) => {
            const { subStartDate, subEndDate } = period

            if (subStartDate === null && subEndDate === null) return

            dates.push(subStartDate, subEndDate)
        })
    }

    const shouldInclude = dates
        .filter((date) => date !== null)
        .some((date) => compareAsc(new Date(date), startDate) === 1 && compareDesc(new Date(date), endDate) === 1)

    return shouldInclude
}

export const getSubDatePadding = (
    isSubStartDate: boolean,
    isSubEndDate: boolean,
    isSubStartDateBeforeCalendarStart: boolean,
    isSubEndDateAfterCalendarEnd: boolean,
) => {
    if (isSubStartDate && isSubEndDate) {
        if (isSubStartDateBeforeCalendarStart) {
            return '0px 8px 8px 0px'
        }
        if (isSubEndDateAfterCalendarEnd) {
            return '8px 0px 0px 8px'
        }
        return '8px 8px 8px 8px'
    }
    if (isSubStartDate && !isSubStartDateBeforeCalendarStart) {
        return '8px 0px 0px 8px'
    }
    if (isSubEndDate && !isSubEndDateAfterCalendarEnd) {
        return '0px 8px 8px 0px'
    }
    return '0px'
}

export const getShowSubLabel = (
    isSubDate: boolean,
    showSubLabelDate: Date,
    subStartDate: Date,
    isSubStartDate: boolean,
    date: Date,
) => {
    if (isSubDate && compareDesc(showSubLabelDate, subStartDate) === 1 && isSubStartDate) {
        return true
    }
    return isSubDate && isSameDay(showSubLabelDate, date)
}

export const getEntityDates = (entity: IssueEntity | WarrantListItem, startDate: Date, endDate: Date) => {
    let entitySubStartDate: string | null = null
    let entitySubEndDate: string | null = null
    if ('type' in entity) {
        entitySubStartDate = entity.subStartDate
        entitySubEndDate = entity.subEndDate
    } else {
        const subscriptionPeriod = entity.subscriptionPeriods.find((subscriptionPeriod) =>
            [subscriptionPeriod.subStartDate, subscriptionPeriod.subEndDate]
                .filter((date) => date !== null)
                .some(
                    (date) => compareAsc(new Date(date), startDate) === 1 && compareDesc(new Date(date), endDate) === 1,
                ),
        )

        entitySubStartDate = subscriptionPeriod?.subStartDate || null
        entitySubEndDate = subscriptionPeriod?.subEndDate || null
    }

    const isSubStartDateBeforeCalendarStart =
        entitySubStartDate && compareDesc(new Date(entitySubStartDate), startDate) === 1

    const isSubEndDateBeforeCalendarStart = entitySubEndDate && compareDesc(new Date(entitySubEndDate), startDate) === 1

    const isSubEndDateAfterCalendarEnd = entitySubEndDate && compareAsc(new Date(entitySubEndDate), endDate) === 1

    const subStartDate =
        entitySubStartDate && !isSubStartDateBeforeCalendarStart ? new Date(entitySubStartDate) : startDate

    const subEndDate = entitySubEndDate && !isSubEndDateAfterCalendarEnd ? new Date(entitySubEndDate) : endDate

    const daysBetweenSubStartAndEnd = Math.floor((subEndDate.getTime() - subStartDate.getTime()) / (1000 * 3600 * 24))

    const preliminarySubLabelDate = addDays(subStartDate, daysBetweenSubStartAndEnd / 2)

    const subLabelDate =
        'type' in entity && entity.rightsEndDate && isSameDay(preliminarySubLabelDate, new Date(entity.rightsEndDate))
            ? addDays(preliminarySubLabelDate, 1)
            : preliminarySubLabelDate

    return {
        subStartDate,
        subEndDate,
        subLabelDate,
        isSubStartDateBeforeCalendarStart,
        isSubEndDateBeforeCalendarStart,
        isSubEndDateAfterCalendarEnd,
    }
}

export const getCellDates = (
    entity: IssueEntity | WarrantListItem,
    subStartDate: Date,
    subEndDate: Date,
    subLabelDate: Date,
    date: Date,
    isSubEndDateBeforeCalendarStart: boolean,
) => {
    const isExRightsDate = 'type' in entity && entity.exRightsDate && isSameDay(new Date(entity.exRightsDate), date)
    const isIncRightsDate = 'type' in entity && entity.incRightsDate && isSameDay(new Date(entity.incRightsDate), date)
    const isRightsEndDate = 'type' in entity && entity.rightsEndDate && isSameDay(new Date(entity.rightsEndDate), date)
    const isIpoTradingDate =
        'type' in entity && entity.ipoTradingDate && isSameDay(new Date(entity.ipoTradingDate), date)

    const isSubDate =
        subStartDate &&
        subEndDate &&
        (isSameDay(new Date(subStartDate), date) || compareDesc(new Date(subStartDate), date) === 1) &&
        (isSameDay(new Date(subEndDate), date) || compareAsc(new Date(subEndDate), date) === 1)

    const isSubStartDate = subStartDate && isSameDay(new Date(subStartDate), date) && !isSubEndDateBeforeCalendarStart

    const isSubEndDate = subEndDate && isSameDay(new Date(subEndDate), date)

    const showSubLabel = getShowSubLabel(isSubDate, subLabelDate, subStartDate, isSubStartDate, date)

    return {
        isExRightsDate,
        isIncRightsDate,
        isRightsEndDate,
        isIpoTradingDate,
        isSubDate,
        isSubStartDate,
        isSubEndDate,
        showSubLabel,
    }
}

export const getAdvisors = (
    entities: Array<IssueEntity | WarrantListItem>,
    othersThreshold = 3,
    language?: string,
    othersLabel = 'Övriga',
) => {
    const sortLocale = getSortLocale(language)
    const advisorTypes = ['financialAdvisors'] as const
    const advisorsMap: { [key: string]: { [key: string]: DropdownItem & { hasNoName: boolean } } } = {
        financialAdvisors: {},
    }
    const entitiesCount: Record<string, number> = {}

    entities.forEach((entity) => {
        if (entity.financialAdvisors?.length === 0) return

        advisorTypes.forEach((advisorType) => {
            ;(entity[advisorType] || []).forEach((advisor) => {
                if (!advisorsMap[advisorType][advisor.advisorId]) {
                    advisorsMap[advisorType][advisor.advisorId] = {
                        label: advisor.name || '',
                        hasNoName: !!advisor.name,
                        id: advisor.advisorId,
                    }
                    entitiesCount[advisor.advisorId] = 0
                }

                entitiesCount[advisor.advisorId]++
            })
        })
    })

    const advisors = advisorTypes.reduce((acc, advisorType) => {
        return {
            ...acc,
            [advisorType]: [
                ...Object.values(advisorsMap[advisorType]).filter(
                    (advisor) => entitiesCount[advisor.id] >= othersThreshold,
                ),
                {
                    id: Object.values(advisorsMap[advisorType])
                        .filter(
                            (advisor) =>
                                entitiesCount[advisor.id] < othersThreshold || entitiesCount[advisor.id].hasNoName,
                        )
                        .map(({ id }) => id)
                        .join(','),
                    label: othersLabel,
                },
            ].sort((a, b) => a.label.localeCompare(b.label, sortLocale)),
        }
    }, {}) as { [key in (typeof advisorTypes)[number]]: DropdownItem[] }

    return advisors
}

const hasIssueFinancialAdvisor = (entity: IssueEntity | ExtendedWarrantEntity, advisorId: string) => {
    if (!entity.financialAdvisors) {
        return false
    }
    return entity.financialAdvisors.find((advisor) => advisorId.split(',').includes(advisor.advisorId))
}

const handleCompletedFilter = (
    entity: IssueEntity | ExtendedWarrantEntity,
    filters: typeof calendarFiltersAtom.init,
) => {
    const today = startOfDay(new Date())
    if ('type' in entity) {
        if (filters.completed.value.includes('not-completed')) {
            return entity.subEndDate ? compareAsc(new Date(entity.subEndDate), today) === 1 : false
        }
        return entity.subEndDate ? compareAsc(today, new Date(entity.subEndDate)) === 1 : false
    }

    const includeWarrant = entity.activeSubscriptionPeriods.some((subscriptionPeriod) => {
        if (filters.completed.value.includes('not-completed')) {
            return subscriptionPeriod.subEndDate
                ? compareAsc(new Date(subscriptionPeriod.subEndDate), today) === 1
                : false
        } else {
            return subscriptionPeriod.subEndDate
                ? compareAsc(today, new Date(subscriptionPeriod.subEndDate)) === 1
                : false
        }
    })

    return includeWarrant
}

export const filterEntities = (
    fetchedIssues: IssueEntity[],
    fetchedWarrants: WarrantListItem[],
    startDate: Date,
    endDate: Date,
    filters: typeof calendarFiltersAtom.init,
) => {
    const calendarEntities = [...fetchedIssues, ...fetchedWarrants]
        .reduce((list: Array<IssueEntity | ExtendedWarrantEntity>, entity: IssueEntity | WarrantListItem) => {
            const includeIssue = shouldIncludeEntity(entity, startDate, endDate)

            if (includeIssue) {
                if ('type' in entity) {
                    if (!hasSourceOfType(entity, 'Cancellation')) {
                        list.push(entity)
                    }
                } else {
                    const warrant = entity
                    const filteredSubscriptionPeriods = warrant.subscriptionPeriods.filter((period) => {
                        const includePeriod = [period.subStartDate, period.subEndDate]
                            .filter((date) => date !== null)
                            .some(
                                (date) =>
                                    compareAsc(new Date(date), startDate) === 1 &&
                                    compareDesc(new Date(date), endDate) === 1,
                            )
                        return includePeriod && !warrant.cancelled
                    })

                    list.push({
                        ...warrant,
                        activeSubscriptionPeriods: filteredSubscriptionPeriods,
                    })
                }
            }

            return list
        }, [])
        .sort((a: IssueEntity | ExtendedWarrantEntity, b: IssueEntity | ExtendedWarrantEntity) => {
            const subStartDateA = 'type' in a ? a.subStartDate : a.activeSubscriptionPeriods[0].subStartDate
            const subStartDateB = 'type' in b ? b.subStartDate : b.activeSubscriptionPeriods[0].subStartDate
            const subEndDateA = 'type' in a ? a.subEndDate : a.activeSubscriptionPeriods[0].subEndDate
            const subEndDateB = 'type' in b ? b.subEndDate : b.activeSubscriptionPeriods[0].subEndDate

            if (
                new Date(subStartDateA).getTime() === new Date(subStartDateB).getTime() ||
                !subStartDateA ||
                !subStartDateB
            ) {
                if (
                    new Date(subEndDateA).getTime() === new Date(subEndDateB).getTime() ||
                    !subEndDateA ||
                    !subEndDateB
                ) {
                    const instrumentNameA = 'type' in a ? a.shareName : a.instrumentName || `${a.companyName} ${a.name}`
                    const instrumentNameB = 'type' in b ? b.shareName : b.instrumentName || `${b.companyName} ${b.name}`

                    return instrumentNameA.localeCompare(instrumentNameB)
                }

                return new Date(subEndDateA).getTime() - new Date(subEndDateB).getTime()
            }

            return new Date(subStartDateA).getTime() - new Date(subStartDateB).getTime()
        })

    const filteredEntities = calendarEntities.filter((entity) => {
        return (
            (filters.issueTypes.value.length === 0 ||
                filters.issueTypes.value.includes('type' in entity ? entity.type : 'Warrant')) &&
            (filters.listed.value.includes('all') || 'type' in entity || filters.listed.value.includes('only-listed')
                ? !entity.unlisted
                : entity.unlisted) &&
            (filters.completed.value.includes('all') || handleCompletedFilter(entity, filters)) &&
            (filters.marketplaces.value.length === 0 || filters.marketplaces.value.includes(entity.marketplaceId)) &&
            (filters.financialAdvisors.value.length === 0 ||
                filters.financialAdvisors.value.some((advisor) => hasIssueFinancialAdvisor(entity, advisor)))
        )
    })

    return { allEntities: calendarEntities, filteredEntities }
}

export const getEntitySize = (
    entity: EnrichedIssueEntity | ExtendedWarrantEntity,
    startDate: Date,
    locale: string,
    translate: (key: string) => string,
) => {
    let size: React.ReactNode = null
    let showPrelPrice: boolean = false

    if ('type' in entity) {
        if (entity.totalOfferedUnits === null && entity.abbTarget !== null) {
            size = formatBigMoney(entity.abbTarget, {
                locale,
                currencySymbol: entity.currency,
                decimals: 0,
                tooltip: true,
            })
        } else if (entity.totalOfferedUnits > 0 || entity.secondaryOffering > 0) {
            let unitSubPrice = entity.unitSubPrice

            if (!entity.unitSubPrice && entity.ipoIntervalLow && entity.ipoIntervalHigh) {
                const ipoIntervalLow = parseFloat(entity.ipoIntervalLow)
                const ipoIntervalHigh = parseFloat(entity.ipoIntervalHigh)
                unitSubPrice = (ipoIntervalLow + ipoIntervalHigh) / 2
            }
            size = formatBigMoney((entity.totalOfferedUnits + entity.secondaryOffering) * unitSubPrice, {
                locale,
                currencySymbol: entity.currency,
                decimals: 0,
                tooltip: true,
            })
        } else if (entity.totalOfferedUnits === 0) {
            size = translate('pages.calendar.size.noNewShares')
        } else {
            size = translate('pages.calendar.size.notYetDetermined')
        }
    } else {
        const { subscriptionPeriods, totalNumberOfWarrants } = entity

        const previousPeriods = subscriptionPeriods.filter(
            (period) => period.subEndDate && new Date(period.subEndDate) < startDate,
        )

        const previouslyExercisedWarrants = previousPeriods.reduce(
            (acc, { sharesSubscribed, sharesReceived, warrantsRequired }) => {
                if (sharesSubscribed === null || sharesReceived === null || warrantsRequired === null) return acc

                return acc + (sharesSubscribed * warrantsRequired) / sharesReceived
            },
            0,
        )

        const remainingWarrants = totalNumberOfWarrants - previouslyExercisedWarrants

        showPrelPrice = entity.activeSubscriptionPeriods.some((period) => hasSubPeriodPrelPrice(period))

        const { prelSubPrice, sharesReceived, warrantSubPrice, warrantsRequired } = entity.activeSubscriptionPeriods[0]
        const price = showPrelPrice ? prelSubPrice : warrantSubPrice

        const amount = ((+price * remainingWarrants) / warrantsRequired) * sharesReceived

        size = formatBigMoney(amount, {
            locale,
            currencySymbol: entity.currency,
            decimals: 0,
            tooltip: true,
        })
    }

    return { size, showPrelPrice }
}
